/**
 * Voxel terrain constants shared between client and server
 */
// ============== Chunk Dimensions ==============
/** Number of voxels per chunk axis */
export const CHUNK_SIZE = 32;
/** Meters per voxel */
export const VOXEL_SCALE = 0.25;
/** World size of a chunk in meters (CHUNK_SIZE * VOXEL_SCALE) */
export const CHUNK_WORLD_SIZE = CHUNK_SIZE * VOXEL_SCALE; // 8m
/** log2(CHUNK_SIZE): global voxel index → chunk coord is `v >> CHUNK_SHIFT`. */
export const CHUNK_SHIFT = 31 - Math.clz32(CHUNK_SIZE); // 5
/** In-chunk voxel index from a global voxel index: `v & CHUNK_LOCAL_MASK`. */
export const CHUNK_LOCAL_MASK = CHUNK_SIZE - 1; // 31
/** 1 / CHUNK_WORLD_SIZE — world metres → chunk coord without a divide (exact: 8 m ⇒ 0.125). */
export const INV_CHUNK_WORLD_SIZE = 1 / CHUNK_WORLD_SIZE; // 0.125
/** Total voxels per chunk (CHUNK_SIZE^3) */
export const VOXELS_PER_CHUNK = CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE; // 32768
// ============== Voxel Bit Layout ==============
// Layout (32-bit word):
//   bit:  0-4    5-11      12-15   16-20        21-31
//         LLLLL  MMMMMMM   WWWW    BBBBB        (spare, 11 bits for future)
//         sky    material  weight  block-light
//
// The low 16 bits (sky light + material + weight) are byte-identical to the
// previous 16-bit layout, so every existing pack/unpack shift & mask is unchanged.
// Block light lives at bit 16; the top 11 bits are reserved for future per-voxel data.
/** Bits for weight field (surface distance) */
const WEIGHT_BITS = 4; // internal derivation base (WEIGHT_MAX_PACKED is the exported face)
/** Bits for material ID field */
export const MATERIAL_BITS = 7;
/** Bits for (sky) light level field */
export const LIGHT_BITS = 5;
/** Bits for block-light field */
const BLOCK_LIGHT_BITS = 5; // internal derivation base (BLOCK_LIGHT_MAX is the exported face)
/** Maximum weight value in packed form */
export const WEIGHT_MAX_PACKED = (1 << WEIGHT_BITS) - 1; // 15
/** Maximum material ID value */
export const MATERIAL_MAX = (1 << MATERIAL_BITS) - 1; // 127
/** Maximum (sky) light level value */
export const LIGHT_MAX = (1 << LIGHT_BITS) - 1; // 31
/** Maximum block-light level value */
export const BLOCK_LIGHT_MAX = (1 << BLOCK_LIGHT_BITS) - 1; // 31
/** Bit shift for material field */
export const MATERIAL_SHIFT = LIGHT_BITS; // 5
/** Bit shift for weight field */
export const WEIGHT_SHIFT = LIGHT_BITS + MATERIAL_BITS; // 12
/** Bit shift for block-light field (first bit past the original 16-bit word) */
export const BLOCK_LIGHT_SHIFT = 16;
/** Bit mask for (sky) light field */
export const LIGHT_MASK = LIGHT_MAX; // 0b11111
/** Bit mask for material field (before shift) */
export const MATERIAL_MASK = MATERIAL_MAX; // 0b1111111
/** Bit mask for weight field (before shift) */
export const WEIGHT_MASK = WEIGHT_MAX_PACKED; // 0b1111
/** Bit mask for block-light field (before shift) */
export const BLOCK_LIGHT_MASK = BLOCK_LIGHT_MAX; // 0b11111
/** Clear mask for the block-light field (positioned, for read-modify-write) */
export const BLOCK_LIGHT_CLEAR = ~(BLOCK_LIGHT_MASK << BLOCK_LIGHT_SHIFT);
/**
 * Mask for the "static" low 16 bits (weight + material + sky light) of the word.
 * Used to strip block-light/spare bits before indexing the material/weight LUTs,
 * which are sized 1<<(16-LIGHT_BITS) and indexed by `word >> LIGHT_BITS`.
 */
export const VOXEL_STATIC_MASK = 0xFFFF;
/** Packed weight threshold for surface (weight=0 boundary) */
export const SURFACE_PACKED_THRESHOLD = WEIGHT_MAX_PACKED >> 1; // 7
/** Precomputed 1/WEIGHT_MAX_PACKED for fast weight unpacking */
export const INV_WEIGHT_MAX_PACKED = 1.0 / WEIGHT_MAX_PACKED; // ~0.0667
// ============== Weight Range ==============
/** Minimum weight value in world units */
export const WEIGHT_MIN = -0.5;
/** Maximum weight value in world units */
const WEIGHT_MAX = 0.5; // internal derivation base (WEIGHT_RANGE is the exported face)
/** Weight range for mapping */
export const WEIGHT_RANGE = WEIGHT_MAX - WEIGHT_MIN; // 1.0
// ============== Visibility Culling ==============
/**
 * Maximum supported view distance, in chunks. The visibility BFS pre-allocates its grid buffers
 * one chunk LARGER per side (GRID_DIAMETER = 2*(R+1)+1) because its traversal cut is the
 * octet-aligned box of the active radius, which reaches R+1 on axes where the alignment rounds
 * outward. This MUST be >= the largest selectable view distance (VIEW_DISTANCES in QualityPresets,
 * which asserts the relationship at module load) — the BFS clips traversal at the grid edge, so a view
 * distance beyond this would silently fail to reach. Also the initial/fallback radius before quality
 * settings apply.
 *
 * Sized to the largest OFFERED view distance, not a comfortable margin above it: the BFS scratch grid
 * is (VISIBILITY_RADIUS+1)³ cells and is walked every time the streaming centre crosses a chunk, so
 * every unit costs ~15% more cells at this size.
 */
export const VISIBILITY_RADIUS = 9;
/** Buffer distance beyond visible before unloading */
export const VISIBILITY_UNLOAD_BUFFER = 1;
/**
 * Face indices for visibility graph.
 * Used to encode which faces can see each other through a chunk.
 */
export var ChunkFace;
(function (ChunkFace) {
    ChunkFace[ChunkFace["POS_X"] = 0] = "POS_X";
    ChunkFace[ChunkFace["NEG_X"] = 1] = "NEG_X";
    ChunkFace[ChunkFace["POS_Y"] = 2] = "POS_Y";
    ChunkFace[ChunkFace["NEG_Y"] = 3] = "NEG_Y";
    ChunkFace[ChunkFace["POS_Z"] = 4] = "POS_Z";
    ChunkFace[ChunkFace["NEG_Z"] = 5] = "NEG_Z";
})(ChunkFace || (ChunkFace = {}));
/** Number of chunk faces */
export const CHUNK_FACE_COUNT = 6;
/** All face pairs visible (empty chunk) */
export const VISIBILITY_ALL = 0x7FFF;
/** No face pairs visible (solid chunk) */
export const VISIBILITY_NONE = 0x0000;
// ============== Meshing ==============
/**
 * High-side voxel layers a mesh borrows from its + neighbour: one lattice layer for the shared plane
 * plus the cell that straddles it. The cross-LOD seam reads voxel signs, not deep margins
 * (docs/cross-lod-seam-redesign.md §0).
 */
export const MESH_MARGIN = 2;
/** Grid dimension: CHUNK_SIZE + MESH_MARGIN */
export const GRID_SIZE = CHUNK_SIZE + MESH_MARGIN;
// ============== Canonical Neighbor Offsets ==============
/** 6 face-adjacent neighbor offsets: +X, -X, +Y, -Y, +Z, -Z */
export const FACE_OFFSETS_6 = [
    [1, 0, 0], [-1, 0, 0],
    [0, 1, 0], [0, -1, 0],
    [0, 0, 1], [0, 0, -1],
];
/**
 * Positive-direction face offsets (+X, +Y, +Z).
 * These are the 3 neighbors whose data appears in a chunk's high-side mesh margin.
 * Use for: "which neighbors does MY mesh depend on?" / pending-neighbor checks.
 */
export const POSITIVE_FACE_OFFSETS_3 = [
    [1, 0, 0], [0, 1, 0], [0, 0, 1],
];
/**
 * Negative-direction margin-consumer offsets (7): the 3 faces, 3 edges, and 1 corner of the negative
 * octant. A chunk's mesh reads its +X/+Y/+Z faces, edges, and corner as margin, so a changed chunk's
 * voxels are consumed as margin by exactly these 7 neighbours (plus itself). Their boundary GEOMETRY
 * changes when the chunk changes, so they must fully re-mesh — build preview and commit share this
 * set so their re-mesh sets can't drift. Each offset's non-zero axes are the ones whose LOW margin the
 * neighbour reads (used to test which drawn-chunk sub-region a neighbour actually consumes).
 */
export const NEGATIVE_MARGIN_OFFSETS_7 = [
    [-1, 0, 0], [0, -1, 0], [0, 0, -1],
    [-1, -1, 0], [-1, 0, -1], [0, -1, -1],
    [-1, -1, -1],
];
/**
 * Positive-direction margin-source offsets (7): the 3 faces, 3 edges, and 1 corner of the POSITIVE
 * octant — exactly the neighbours a chunk's mesh reads as its high-side (+X/+Y/+Z) margin (see
 * expandChunkData). A chunk must have all 7 present to mesh its boundary without extrapolation; the
 * mirror of NEGATIVE_MARGIN_OFFSETS_7 (which are the chunks that consume THIS chunk as their margin).
 */
export const POSITIVE_MARGIN_OFFSETS_7 = [
    [1, 0, 0], [0, 1, 0], [0, 0, 1],
    [1, 1, 0], [1, 0, 1], [0, 1, 1],
    [1, 1, 1],
];
/**
 * ================= CHUNK DEPENDENCY CONTRACT (read before touching streaming/meshing) =================
 *
 * A chunk's surface mesh is NOT a function of its own voxels alone — it is a function of the chunk AND
 * a fixed set of neighbours. Every subsystem that loads, meshes, invalidates, or renders chunks must
 * agree on that neighbour set, or the derived representations drift apart and holes/pops appear. These
 * offset tables are the single source of truth; the four consumers below MUST all derive from them:
 *
 *   1. READ  (mesh input)     — expandChunkData reads POSITIVE_MARGIN_OFFSETS_7 as the high-side margin.
 *   2. WAIT  (mesh readiness) — a chunk defers meshing until its POSITIVE_MARGIN_OFFSETS_7 are resolved.
 *   3. INVALIDATE (re-mesh)   — when a chunk changes, its NEGATIVE_MARGIN_OFFSETS_7 consumers re-mesh.
 *                               The two tables are INDEX-WISE MIRRORS (NEGATIVE[i] === −POSITIVE[i]),
 *                               which is what lets an arrival say precisely WHICH source a consumer
 *                               was missing: the consumer at negative offset i is the one whose
 *                               `Chunk.extrapolatedMargins` bit i names the arriving chunk. Pinned by
 *                               a test in marginExpand.test.ts — if the arrays ever drift, the
 *                               re-triggers heal the wrong edge and cracks return silently.
 *                               NOTE: "consumer needs re-meshing" is NOT the same as
 *                               "consumer's mesh is incomplete". Mesh completeness is derived from
 *                               the 3 FACE sources only (an unbuilt face is a hole, so such a mesh
 *                               never draws), while the 4 DIAGONAL sources feed a one-vertex-wide
 *                               edge/corner column: a chunk missing only a diagonal is topologically
 *                               whole, DRAWS, and is geometrically wrong there. Gating re-triggers
 *                               and healers on the phase alone therefore left those cracks permanent
 *                               — hence the per-source mask.
 *                               NOTE 2: the mask a consumer publishes describes the mesh ON SCREEN,
 *                               and a mesh takes frames to build (grid expanded at dispatch, phase and
 *                               mask published at apply). A source arriving inside that window is
 *                               invisible to a trigger that reads the CHUNK — a first mesh has
 *                               published nothing at all — so the trigger must read what in-flight
 *                               dispatches extrapolated too (RemeshPipeline.extrapolatedMarginsFor,
 *                               marginConvergence.ts). Reading the chunk alone made every such arrival
 *                               a permanent crack; pinned by meshOrderIndependence.test.ts.
 *   4. LOAD/RENDER (coverage) — a chunk may only be rendered once its margin sources exist, so loading
 *                               must stay one ring AHEAD of rendering (render ⊆ load − 1 ring). The
 *                               render/load one-ring dilation uses FACE_OFFSETS_6.
 *
 * BOUNDARY OWNERSHIP: the shared face between chunk A and its +axis neighbour B is meshed ONLY by A
 * (A's high face, built from B's voxels); B skips its low face. So a boundary's surface belongs to the
 * LOWER-coordinate chunk. Consequences:
 *   - A rendered chunk needs its +margin neighbours LOADED (to build its own high faces) and its lower
 *     neighbours RENDERED (they own the surface on its low faces). Hence the load set must dilate the
 *     render set on all sides, and rendering must be gated on mesh completeness (no skipped high faces).
 *   - Meshing a chunk with an absent +neighbour SKIPS that high face (skipHighBoundary) → a hole that
 *     only heals when the neighbour streams in and the chunk re-meshes. Never render such a mesh.
 * =====================================================================================================
 */
//# sourceMappingURL=constants.js.map